import React from 'react';
import ReactDOM from 'react-dom/client';

// Riutilizzo componenti dal ProductWidget (grazie agli alias in vite.config.ts)
import StripeCheckout from '@components/Form/StripeCheckout';
import PayPalCheckoutProduct from '@components/Form/PayPalCheckout';

import LocalizationProvider, { useLocalization } from '@providers/LocalizationProvider';

declare global {
  interface Window {
    __GARFY_CHECKOUT_EXPRESS__?: {
      stripe?: { publishableKey?: string };
      paypal?: { clientId?: string; mode?: 'sandbox' | 'live'; sdkUrl?: string };
      urls?: {
        stripePaymentIntent?: string;
        paypalCreateOrder?: string;
        paypalCompleteCheckout?: string;
        paypalCapturePayment?: string;
        paypalCurrentOrder?: string;
        paypalCaptureOrder?: string;
      };
      wc?: {
        currency?: string;
        locale?: string;
        isDelivery?: boolean;
        orderContextId?: string | null;
        amountCents?: number;
        context?: 'checkout' | 'cart' | 'hold';
        excludeExpressCheckout?: boolean;
      };
      consent?: { termsSelector?: string };
      nonce?: string;
    };
  }
}

// ===== Config iniettata da Manager.php =====
const cfg = window.__GARFY_CHECKOUT_EXPRESS__ || {};

const stripePk = cfg?.stripe?.publishableKey || '';
const ppClientId = cfg?.paypal?.clientId || '';
const amountCents = Number(cfg?.wc?.amountCents ?? 0);
const isDelivery = Boolean(cfg?.wc?.isDelivery);
const currency = (cfg?.wc?.currency || 'EUR').toUpperCase();
const phpLocale = (cfg?.wc?.locale || 'it_IT').replace('-', '_'); // locale di fallback 
const context =  cfg?.wc?.context || '';


 console.log('[CheckoutExpress] config:', {
 /*  stripePk,
  ppClientId,
  amountCents, */
  isDelivery,
  /* currency,
  phpLocale,
  context, */
}); 

// ===== PayPal options (base, senza lingua) =====
const origin = window.location.origin;

const paypalOptionsBase = {
  client_id: ppClientId,

  // preferisci i nuovi endpoint dalla config PHP, ma con fallback
  createOrderUrl:
    cfg?.urls?.paypalCreateOrder ||
    `${origin}/wp-json/garfy/v1/paypal/create-order`,

  completeCheckoutUrl:
    cfg?.urls?.paypalCompleteCheckout ||
    `${origin}/wp-json/garfy/v1/paypal/complete-checkout`,

  // legacy (se PayPalCheckoutProduct lo usa ancora)
  paymentCaptureUrl:
    cfg?.urls?.paypalCapturePayment ||
    `${origin}/wp-json/garfy/v1/paypal/capture-payment`,

  // nuovi (se supportati dal componente)
  currentOrderUrl:
    cfg?.urls?.paypalCurrentOrder ||
    `${origin}/wp-json/garfy/v1/paypal/current-order`,

  captureOrderUrl:
    cfg?.urls?.paypalCaptureOrder ||
    `${origin}/wp-json/garfy/v1/paypal/capture-order`,

  currency,
  locale: phpLocale, // fallback 
};

// Avviso chiaro se mancano i nuovi endpoint attesi
if (!cfg?.urls?.paypalCurrentOrder || !cfg?.urls?.paypalCaptureOrder) {
  console.warn(
    '[CheckoutExpress] URL PayPal mancanti in config: aggiorna Manager.php (paypalCurrentOrder/paypalCaptureOrder).'
  );
}

const stripePaymentIntentUrl =
  cfg?.urls?.stripePaymentIntent ||
  `${origin}/wp-json/garfy/v1/stripe/payment-intent`;

// ===== App =====
function App() {
  const showStripe = !!stripePk;
  const showPayPal = !!ppClientId;

  const { language } = useLocalization();

  // Stripe: options costruite usando la lingua del LocalizationProvider,
  // esattamente come fai in Submit
  const stripeElementsOptions = {
    mode: 'payment' as const,
    amount: amountCents,
    currency: currency.toLowerCase(),
    locale: language || phpLocale, // se language manca, uso il fallback PHP
    appearance: { theme: 'stripe' as const },
  };

  // Costruisce il metadata tenendo conto del contesto (hold vs checkout/cart)
  const stripeMetadata: Record<string, string> = {
    context: context || 'checkout',
  };

  const stripeOptions = {
    ...stripeElementsOptions,
    metadata: stripeMetadata,
  };

  // PayPal: partiamo dalla base e aggiorniamo la locale con quella del LocalizationProvider
  const paypalOptions = {
    ...paypalOptionsBase,
    locale: language || paypalOptionsBase.locale,
  };

  if (!showStripe && !showPayPal) {
    // Placeholder
    return (
      <section id="garfy-express-checkout">
        <noscript data-warn="stripe-missing"></noscript>
        <noscript data-warn="paypal-missing"></noscript>
      </section>
    );
  }

  return (
    <section
      className="row row-cols-1 row-cols-md-2"
      id="garfy-express-checkout"
    >
      {showPayPal && (
        <div className="col">
          <PayPalCheckoutProduct
            productId={0}
            amountCents={amountCents}
            extraMetadata={{ context: 'checkout' }}
            isDelivery={isDelivery}
            paypalOptions={paypalOptions}
          />
        </div>
      )}

      {showStripe && (
        <div className="col">
          <StripeCheckout
            stripeApikey={stripePk}
            elementsOptions={stripeElementsOptions}
            stripeOptions={stripeOptions}
            stripePaymentIntentUrl={stripePaymentIntentUrl}
          />
        </div>
      )}
    </section>
  );
}

// ===== Mount =====
const root = document.getElementById('garfy-express-checkout-root');
if (root) {
  ReactDOM.createRoot(root).render(
    <LocalizationProvider>
      <App />
    </LocalizationProvider>
  );
} else {
  console.warn(
    '[CheckoutExpress] Mount point #garfy-express-checkout-root non trovato.'
  );
}
